import dayjs from "dayjs";

/** Built-in presets available in System Settings and list filters. */
export const DATE_FILTER_PRESET_DEFS = [
  { key: "today", labelKey: "dashboardPresetToday", defaultLabel: "Today" },
  { key: "yesterday", labelKey: "datePresetYesterday", defaultLabel: "Yesterday" },
  { key: "2d", labelKey: "datePreset2d", defaultLabel: "2 Days" },
  { key: "7d", labelKey: "dashboardPreset7d", defaultLabel: "7 Days" },
  { key: "21d", labelKey: "datePreset21d", defaultLabel: "3 Weeks" },
  { key: "last_week", labelKey: "datePresetLastWeek", defaultLabel: "Last Week" },
  { key: "30d", labelKey: "dashboardPreset30d", defaultLabel: "30 Days" },
  { key: "this_month", labelKey: "dashboardPresetMonth", defaultLabel: "This Month" },
  { key: "last_month", labelKey: "datePresetLastMonth", defaultLabel: "Last Month" },
];

export const DEFAULT_DATE_FILTER_PRESETS = [
  "today",
  "2d",
  "7d",
  "last_week",
  "this_month",
  "30d",
];

const VALID_KEYS = new Set(DATE_FILTER_PRESET_DEFS.map((d) => d.key));

/** Parse presets from system config (array, CSV, or JSON string). */
export function normalizeDateFilterPresets(raw) {
  let list = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === "string" && raw.trim()) {
    const s = raw.trim();
    if (s.startsWith("[")) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) list = parsed;
      } catch {
        list = s.split(/[,|]/).map((x) => x.trim()).filter(Boolean);
      }
    } else {
      list = s.split(/[,|]/).map((x) => x.trim()).filter(Boolean);
    }
  }
  const cleaned = list.map((k) => String(k).trim()).filter((k) => VALID_KEYS.has(k));
  return cleaned.length ? cleaned : [...DEFAULT_DATE_FILTER_PRESETS];
}

export function getEnabledDateFilterPresets(config) {
  return normalizeDateFilterPresets(config?.date_filter_presets);
}

/**
 * Resolve a preset key to [start, end] dayjs range.
 * When withTime is true, start is startOf day (or exact) and end is endOf day / now.
 */
export function resolveDatePreset(key, { withTime = true, now = dayjs() } = {}) {
  const endNow = dayjs(now);
  let start;
  let end = withTime ? endNow.endOf("day") : endNow;

  switch (key) {
    case "today":
      start = endNow.startOf("day");
      end = withTime ? endNow.endOf("day") : endNow;
      break;
    case "yesterday": {
      const y = endNow.subtract(1, "day");
      start = y.startOf("day");
      end = y.endOf("day");
      break;
    }
    case "2d":
      start = endNow.subtract(1, "day").startOf("day");
      end = withTime ? endNow.endOf("day") : endNow;
      break;
    case "7d":
      start = endNow.subtract(6, "day").startOf("day");
      end = withTime ? endNow.endOf("day") : endNow;
      break;
    case "21d":
      start = endNow.subtract(20, "day").startOf("day");
      end = withTime ? endNow.endOf("day") : endNow;
      break;
    case "last_week": {
      const startOfThisWeek = endNow.startOf("week");
      start = startOfThisWeek.subtract(1, "week");
      end = startOfThisWeek.subtract(1, "day").endOf("day");
      break;
    }
    case "30d":
      start = endNow.subtract(29, "day").startOf("day");
      end = withTime ? endNow.endOf("day") : endNow;
      break;
    case "this_month":
    case "month":
      start = endNow.startOf("month");
      end = withTime ? endNow.endOf("day") : endNow;
      break;
    case "last_month": {
      const prev = endNow.subtract(1, "month");
      start = prev.startOf("month");
      end = prev.endOf("month");
      break;
    }
    default:
      start = endNow.startOf("day");
      end = withTime ? endNow.endOf("day") : endNow;
  }

  return [start, end];
}

export const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";
export const DATE_ONLY_FORMAT = "YYYY-MM-DD";

/** Serialize range for API query params. */
export function formatRangeParams(range, { withTime = true, fromKey = "date_from", toKey = "date_to" } = {}) {
  const params = {};
  if (!range?.[0] && !range?.[1]) return params;
  const fmt = withTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD";
  if (range?.[0]) params[fromKey] = range[0].format(fmt);
  if (range?.[1]) params[toKey] = range[1].format(fmt);
  return params;
}
